import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
} from '@mui/material';
import {
  ContentPaste,
  Close,
  ArrowForward,
  ArrowBack,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../theme/colors';
import { flagEmoji } from '../utils/countryFlags';
import type { PlayerPoolEntry } from '../types/database';

interface ParsedTennisPlayer {
  playerId: string;
  name: string;
  country: string;
  seed: number | undefined;
  gender: 'men' | 'women';
  price: number | '';
  isDuplicate: boolean;
}

interface TennisPoolImportDialogProps {
  open: boolean;
  onClose: () => void;
  existingPlayerIds: string[];
  onImport: (players: Omit<PlayerPoolEntry, 'lastUpdated' | 'updatedBy'>[]) => Promise<void>;
}

const FORMAT_EXAMPLE = `# Men
1 | Jannik Sinner | ITA | 18
2 | Carlos Alcaraz | ESP | 17
3 | Alexander Zverev | GER | 14

# Women
1 | Iga Swiatek | POL | 18
2 | Aryna Sabalenka | BLR | 16
3 | Coco Gauff | USA | 14`;

function parseRawText(text: string, existingIds: string[]): { rows: ParsedTennisPlayer[]; skipped: number } {
  const lines = text.split('\n');
  const rows: ParsedTennisPlayer[] = [];
  let currentGender: 'men' | 'women' = 'men';
  let skipped = 0;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Section header detection
    const lower = line.toLowerCase();
    if (lower.includes('wom')) {
      currentGender = 'women';
      continue;
    }
    if (lower.startsWith('#') || lower === 'men' || lower === 'men\'s') {
      currentGender = 'men';
      continue;
    }
    // "men" appears as a standalone section header but not inside player names
    // Only treat as header if the trimmed line is just a gender label (no | separator)
    if (!line.includes('|') && !line.includes('\t') && lower.match(/^men'?s?\s*$/)) {
      currentGender = 'men';
      continue;
    }

    // Detect separator: prefer | over tab
    const sep = line.includes('|') ? '|' : '\t';
    const parts = line.split(sep).map(s => s.trim());

    // Need at least a name (col 1 if seed present, col 0 if not)
    // Try: [seed, name, country?, price?] or [name, country?, price?]
    let seed: number | undefined;
    let name = '';
    let country = '';
    let price: number | '' = '';

    const first = parts[0];
    const seedCandidate = parseInt(first.replace(/\D/g, ''), 10);

    if (!isNaN(seedCandidate) && seedCandidate > 0 && parts.length > 1) {
      // First column is a seed
      seed = seedCandidate;
      name = parts[1] ?? '';
      country = parts[2] ?? '';
      const priceRaw = parseInt((parts[3] ?? '').replace(/\D/g, ''), 10);
      price = !isNaN(priceRaw) && priceRaw > 0 ? priceRaw : '';
    } else {
      // No leading seed
      name = parts[0] ?? '';
      country = parts[1] ?? '';
      const priceRaw = parseInt((parts[2] ?? '').replace(/\D/g, ''), 10);
      price = !isNaN(priceRaw) && priceRaw > 0 ? priceRaw : '';
    }

    if (!name) {
      skipped++;
      continue;
    }

    const playerId = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    rows.push({
      playerId,
      name,
      country,
      seed,
      gender: currentGender,
      price,
      isDuplicate: existingIds.includes(playerId),
    });
  }

  return { rows, skipped };
}

const TennisPoolImportDialog: React.FC<TennisPoolImportDialogProps> = ({
  open,
  onClose,
  existingPlayerIds,
  onImport,
}) => {
  const [step, setStep] = useState<'paste' | 'review'>('paste');
  const [rawText, setRawText] = useState('');
  const [rows, setRows] = useState<ParsedTennisPlayer[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [formatExpanded, setFormatExpanded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setStep('paste');
    setRawText('');
    setRows([]);
    setSkippedCount(0);
    setError(null);
    setFormatExpanded(false);
    onClose();
  };

  const handleParse = () => {
    if (!rawText.trim()) {
      setError('Paste some player data first.');
      return;
    }
    const { rows: parsed, skipped } = parseRawText(rawText, existingPlayerIds);
    if (parsed.length === 0) {
      setError('No players could be parsed. Check the format and try again.');
      return;
    }
    setRows(parsed);
    setSkippedCount(skipped);
    setError(null);
    setStep('review');
  };

  const handlePriceChange = (idx: number, val: string) => {
    const parsed = parseInt(val, 10);
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, price: !isNaN(parsed) && parsed > 0 ? parsed : '' } : r));
  };

  const handleRemoveRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const importable = rows.filter(r => !r.isDuplicate);
  const allPriced = importable.every(r => typeof r.price === 'number' && r.price > 0);
  const unpricedCount = importable.filter(r => r.price === '').length;
  const menCount = rows.filter(r => r.gender === 'men' && !r.isDuplicate).length;
  const womenCount = rows.filter(r => r.gender === 'women' && !r.isDuplicate).length;
  const dupCount = rows.filter(r => r.isDuplicate).length;

  const handleImport = async () => {
    if (!allPriced) return;
    setImporting(true);
    setError(null);
    try {
      const toImport = importable.map(r => ({
        playerId: r.playerId,
        name: r.name,
        team: r.country || 'Unknown',
        role: 'batsman' as const,
        points: 0,
        isOverseas: false,
        gender: r.gender,
        seed: r.seed,
        price: r.price as number,
      }));
      await onImport(toImport);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed. Please try again.');
      setImporting(false);
    }
  };

  const dialogSx = {
    '& .MuiDialog-paper': {
      background: colors.gradients.hero,
      border: `1px solid ${colors.border.default}`,
      borderRadius: 2,
      maxWidth: 780,
      width: '100%',
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={dialogSx} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" color="white">
            Quick Import from Draw
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Paste ATP/WTA draw data to bulk-add players with prices
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pb: 1 }}>
        <Stepper activeStep={step === 'paste' ? 0 : 1} sx={{ '& .MuiStepLabel-label': { color: 'text.secondary' } }}>
          <Step>
            <StepLabel>Paste</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review & Import</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {step === 'paste' && (
          <Box>
            {/* Format hint */}
            <Box
              sx={{
                mb: 2,
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  bgcolor: alpha(colors.blue.electric, 0.08),
                  cursor: 'pointer',
                }}
                onClick={() => setFormatExpanded(v => !v)}
              >
                <Typography variant="body2" color="text.secondary">
                  Accepted format: <code style={{ color: colors.blue.light }}>seed | name | country | price</code>
                </Typography>
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  {formatExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={formatExpanded}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    px: 2,
                    py: 1.5,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: colors.text.secondary,
                    bgcolor: alpha(colors.blue.navy, 0.5),
                    overflowX: 'auto',
                    whiteSpace: 'pre',
                  }}
                >
                  {FORMAT_EXAMPLE}
                </Box>
                <Box sx={{ px: 2, py: 1, bgcolor: alpha(colors.blue.navy, 0.3) }}>
                  <Typography variant="caption" color="text.secondary">
                    Sections start with <code># Men</code> or <code># Women</code>. Price can be omitted and set in the review step.
                    Tab-separated format (copy from web tables) is also accepted.
                  </Typography>
                </Box>
              </Collapse>
            </Box>

            <TextField
              label="Paste draw / rankings data"
              multiline
              rows={14}
              fullWidth
              value={rawText}
              onChange={e => setRawText(e.target.value)}
              placeholder={FORMAT_EXAMPLE}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <ContentPaste sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
                sx: { fontFamily: 'monospace', fontSize: '0.82rem', alignItems: 'flex-start' },
              }}
            />
          </Box>
        )}

        {step === 'review' && (
          <Box>
            {/* Summary chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                label={`${menCount} men`}
                size="small"
                sx={{ bgcolor: alpha(colors.blue.electric, 0.15), color: colors.blue.light }}
              />
              <Chip
                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                label={`${womenCount} women`}
                size="small"
                sx={{ bgcolor: alpha('#E91E63', 0.15), color: '#F06292' }}
              />
              {unpricedCount > 0 && (
                <Chip
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  label={`${unpricedCount} need price`}
                  size="small"
                  sx={{ bgcolor: alpha(colors.warning.primary, 0.15), color: colors.warning.primary }}
                />
              )}
              {dupCount > 0 && (
                <Chip
                  label={`${dupCount} already in pool (skipped)`}
                  size="small"
                  sx={{ bgcolor: alpha(colors.grey[500], 0.15), color: colors.grey[400] }}
                />
              )}
              {skippedCount > 0 && (
                <Chip
                  label={`${skippedCount} lines unparseable`}
                  size="small"
                  sx={{ bgcolor: alpha(colors.error.primary, 0.1), color: colors.error.light }}
                />
              )}
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 420,
                border: `1px solid ${colors.border.default}`,
                bgcolor: alpha(colors.blue.navy, 0.4),
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: colors.blue.navy, color: 'white', fontWeight: 600 } }}>
                    <TableCell>Gender</TableCell>
                    <TableCell>Seed</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Price (cr)</TableCell>
                    <TableCell align="center">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={row.playerId + idx}
                      sx={{
                        opacity: row.isDuplicate ? 0.45 : 1,
                        bgcolor: !row.isDuplicate && row.price === ''
                          ? alpha(colors.warning.primary, 0.07)
                          : 'transparent',
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.gender === 'men' ? 'Men' : 'Women'}
                          size="small"
                          sx={{
                            bgcolor: row.gender === 'men'
                              ? alpha(colors.blue.electric, 0.18)
                              : alpha('#E91E63', 0.18),
                            color: row.gender === 'men' ? colors.blue.light : '#F06292',
                            fontSize: '0.68rem',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {row.seed ?? '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 500 }}>
                        {row.name}
                        {row.isDuplicate && (
                          <Chip
                            label="Already in pool"
                            size="small"
                            sx={{ ml: 1, fontSize: '0.65rem', bgcolor: alpha(colors.grey[500], 0.2), color: colors.grey[400] }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {row.country ? `${flagEmoji(row.country)} ${row.country}` : '—'}
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        {row.isDuplicate ? (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={row.price}
                            onChange={e => handlePriceChange(idx, e.target.value)}
                            inputProps={{ min: 1, style: { width: 56, textAlign: 'right' } }}
                            InputProps={{ endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">cr</Typography></InputAdornment> }}
                            error={row.price === ''}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: row.price === '' ? alpha(colors.warning.primary, 0.08) : 'transparent',
                              },
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRow(idx)}
                          sx={{ color: colors.error.light, opacity: 0.7, '&:hover': { opacity: 1 } }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!allPriced && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Set a price for all {unpricedCount} highlighted player{unpricedCount !== 1 ? 's' : ''} before importing.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {step === 'paste' ? (
          <>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleParse}
              disabled={!rawText.trim()}
              sx={{
                background: colors.gradients.button,
                '&:hover': { background: colors.blue.deep },
              }}
            >
              Parse
            </Button>
          </>
        ) : (
          <>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setStep('paste')}
              sx={{ color: 'text.secondary' }}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!allPriced || importable.length === 0 || importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{
                background: allPriced && importable.length > 0 ? colors.gradients.button : undefined,
                '&:hover': { background: colors.blue.deep },
              }}
            >
              {importing ? 'Importing…' : `Import ${importable.length} player${importable.length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TennisPoolImportDialog;
